/**
 * Phone number formatter utility
 * Formats US phone numbers as (XXX) XXX-XXXX
 * Strips non-numeric characters and limits to 10 digits
 */

const PHONE_MAX_LENGTH = 10;

/**
 * Removes all non-numeric characters from input
 */
function stripNonNumeric(value: string): string {
  return value.replace(/\D/g, '');
}

/**
 * Formats a numeric string as (XXX) XXX-XXXX
 * Handles partial input gracefully
 */
function formatPhoneNumber(numericValue: string): string {
  // Limit to 10 digits
  const trimmed = numericValue.slice(0, PHONE_MAX_LENGTH);
  const length = trimmed.length;

  if (length === 0) {
    return '';
  }

  if (length <= 3) {
    return `(${trimmed}`;
  }

  if (length <= 6) {
    return `(${trimmed.slice(0, 3)}) ${trimmed.slice(3)}`;
  }

  return `(${trimmed.slice(0, 3)}) ${trimmed.slice(3, 6)}-${trimmed.slice(6)}`;
}

/**
 * Strips leading country code (1) from 11-digit US phone numbers
 * e.g., "15551234567" becomes "5551234567"
 */
function stripCountryCode(numericValue: string): string {
  // If the number is 11 digits and starts with '1', remove the leading '1'
  if (numericValue.length === 11 && numericValue.startsWith('1')) {
    return numericValue.slice(1);
  }
  return numericValue;
}

/**
 * Main handler for input/paste events on phone number fields
 * Returns the formatted value
 */
function handlePhoneInput(inputElement: HTMLInputElement): string {
  const rawValue = inputElement.value || '';
  const numericOnly = stripNonNumeric(rawValue);
  const withoutCountryCode = stripCountryCode(numericOnly);
  const formatted = formatPhoneNumber(withoutCountryCode);

  inputElement.value = formatted;
  return formatted;
}

/**
 * Attaches formatting handlers to a phone input element
 */
function attachPhoneFormatter(selector: string): void {
  const input = document.querySelector<HTMLInputElement>(selector);

  if (!input) {
    return;
  }

  // Handle typing
  input.addEventListener('input', () => {
    handlePhoneInput(input);
  });

  // Handle paste
  input.addEventListener('paste', () => {
    // Let the paste happen, then format on next tick
    setTimeout(() => {
      handlePhoneInput(input);
    }, 0);
  });
}

export default {
  stripNonNumeric,
  stripCountryCode,
  formatPhoneNumber,
  handlePhoneInput,
  attachPhoneFormatter,
};
